import { QueryClient } from 'react-query';
// React Query v3 experimental persistence (guarded)
let persistQueryClientFn = null;
let createWebStoragePersistorFn = null;
try {
  // eslint-disable-next-line global-require
  persistQueryClientFn = require('react-query/persistQueryClient-experimental').persistQueryClient;
  // eslint-disable-next-line global-require
  createWebStoragePersistorFn = require('react-query/createWebStoragePersistor-experimental').createWebStoragePersistor;
} catch (e) {
  // Optional dependency; ignore if unavailable
}

// Create a global QueryClient instance that can be imported anywhere
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Enable automatic refetch when window regains focus
      refetchOnWindowFocus: true,
      // Retry failed requests
      retry: 1,
      // Consider data stale after 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep data in cache for 10 minutes
      cacheTime: 10 * 60 * 1000,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

// Persist cache for instant dashboard back-navigation
if (typeof window !== 'undefined' && persistQueryClientFn && createWebStoragePersistorFn) {
  try {
    const persistor = createWebStoragePersistorFn({ storage: window.localStorage });
    persistQueryClientFn({ queryClient, persistor, maxAge: 10 * 60 * 1000 });
  } catch (e) {
    // Ignore persistence errors
  }
}

// Debounce invalidation to prevent rapid-fire calls during navigation
let invalidationTimeout = null;

// Helper function to invalidate document-related queries
export const invalidateDocumentQueries = () => {
  console.log('🔄 Invalidating document queries...');
  
  // Clear any pending invalidation
  if (invalidationTimeout) {
    clearTimeout(invalidationTimeout);
  }
  
  // Debounce invalidation to prevent rapid-fire calls
  invalidationTimeout = setTimeout(() => {
    console.log('📊 Executing debounced cache invalidation');
    
    try {
      // Invalidate patterns to ensure document/folder queries are marked stale without forced refetch
      queryClient.invalidateQueries(['documents'], { exact: false });
      queryClient.invalidateQueries(['folders'], { exact: false });
      
      console.log('✅ Document cache invalidation completed');
    } catch (error) {
      console.error('❌ Cache invalidation failed:', error);
    }
    
    invalidationTimeout = null;
  }, 100); // 100ms debounce
};

// Helper function to invalidate signature-related queries
export const invalidateSignatureQueries = (documentId = null) => {
  console.log('🔄 Invalidating signature queries...', documentId ? `for document ${documentId}` : '');
  if (documentId) {
    queryClient.invalidateQueries(['signatures', documentId]);
  } else {
    queryClient.invalidateQueries(['signatures']);
  }
};

// Helper function to invalidate contact queries
export const invalidateContactQueries = () => {
  console.log('🔄 Invalidating contact queries...');
  queryClient.invalidateQueries(['contacts']);
};

// Helper function to invalidate folder queries
export const invalidateFolderQueries = () => {
  console.log('🔄 Invalidating folder queries...');
  queryClient.invalidateQueries(['folders']);
};

// Helper function for optimistic document status updates
export const optimisticallyUpdateDocumentStatus = (documentId, newStatus) => {
  console.log(`🚀 Optimistically updating document ${documentId} status to ${newStatus}`);
  
  // Get all document query keys
  const queryCache = queryClient.getQueryCache();
  const documentQueries = queryCache.findAll(['documents']);
  
  documentQueries.forEach(({ queryKey }) => {
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData || !Array.isArray(oldData)) return oldData;
      
      return oldData.map(doc => 
        doc.id === documentId 
          ? { ...doc, status: newStatus, updatedAt: new Date().toISOString() }
          : doc
      );
    });
  });
};

export default queryClient; 